/**
 * UI module - handles DOM manipulation and user interface updates
 */

import { formatTime, formatTimestamp } from './timer.js';

export class UI {
  constructor() {
    this.elements = {
      // Loading
      loadingState: document.getElementById('loadingState'),
      mainContent: document.getElementById('mainContent'),

      // Buttons
      startBtn: document.getElementById('startBtn'),
      finishBtn: document.getElementById('finishBtn'),
      saveBtn: document.getElementById('saveBtn'),
      clearBtn: document.getElementById('clearBtn'),
      copyBtn: document.getElementById('copyBtn'),

      // Display
      timerDisplay: document.getElementById('timerDisplay'),
      startTimeDisplay: document.getElementById('startTimeDisplay'),
      currentNumber: document.getElementById('currentNumber'),

      // Results
      resultsBody: document.getElementById('resultsBody'),
      emptyState: document.getElementById('emptyState'),
      copyNotification: document.getElementById('copyNotification'),

      // Numpad
      numpadButtons: document.querySelectorAll('.numpad-btn'),
    };
  }

  /**
   * Hide loading, show main content
   */
  hideLoading() {
    this.elements.loadingState.style.display = 'none';
    this.elements.mainContent.style.display = 'block';
  }

  /**
   * Update timer display
   * @param {number} startTime - Race start timestamp
   */
  updateTimer(startTime) {
    const elapsed = Date.now() - startTime;
    this.elements.timerDisplay.textContent = formatTime(elapsed);
  }

  /**
   * Update start time display
   * @param {number} startTime - Race start timestamp
   */
  updateStartTime(startTime) {
    this.elements.startTimeDisplay.textContent = `Start: ${formatTimestamp(startTime)}`;
  }

  /**
   * Reset timer display
   */
  resetTimer() {
    this.elements.timerDisplay.textContent = '00:00:00';
    this.elements.startTimeDisplay.textContent = 'Start: --:--:--';
  }

  /**
   * Update current number display
   * @param {string} number - Current number string
   */
  updateCurrentNumber(number) {
    this.elements.currentNumber.textContent = number || '\u00A0';
  }

  /**
   * Enable/disable start button
   * @param {boolean} enabled
   */
  setStartButtonEnabled(enabled) {
    this.elements.startBtn.disabled = !enabled;
  }

  /**
   * Enable/disable finish button
   * @param {boolean} enabled
   */
  setFinishButtonEnabled(enabled) {
    this.elements.finishBtn.disabled = !enabled;
  }

  /**
   * Enable/disable numpad buttons
   * @param {boolean} enabled
   */
  setNumpadEnabled(enabled) {
    this.elements.numpadButtons.forEach(btn => {
      btn.disabled = !enabled;
    });
    this.elements.saveBtn.disabled = !enabled;
    this.elements.clearBtn.disabled = !enabled;
  }

  /**
   * Add a result row to the table
   * @param {object} result - Result object {number, time, timestamp}
   * @param {number} placement - Placement number
   */
  addResultRow(result, placement) {
    const row = document.createElement('tr');
    row.innerHTML = `
      <td>${placement}</td>
      <td><strong>${result.number}</strong></td>
      <td>${result.time}</td>
    `;
    this.elements.resultsBody.insertBefore(row, this.elements.resultsBody.firstChild);
  }

  /**
   * Clear all results from table
   */
  clearResults() {
    this.elements.resultsBody.innerHTML = '';
  }

  /**
   * Show/hide empty state
   * @param {boolean} show
   */
  showEmptyState(show) {
    this.elements.emptyState.style.display = show ? 'flex' : 'none';
  }

  /**
   * Enable/disable copy button
   * @param {boolean} enabled
   */
  setCopyButtonEnabled(enabled) {
    this.elements.copyBtn.disabled = !enabled;
  }

  /**
   * Show copy notification
   */
  showCopyNotification() {
    const notification = this.elements.copyNotification;
    notification.classList.add('show');
    setTimeout(() => {
      notification.classList.remove('show');
    }, 2000);
  }

  /**
   * Trigger haptic feedback (if supported)
   */
  vibrate() {
    if ('vibrate' in navigator) {
      navigator.vibrate(15);
    }
  }
}
